import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Compact 5-dot lifecycle strip for a single card.
 * Each dot represents a stage (issued · delivered · opened · spent · depleted).
 * - Filled dot = stage completed
 * - Hollow dot = pending
 * Hover/tap a dot to see the timestamp tooltip.
 *
 * Lazily loads /cards/{id}/lifecycle on mount; renders nothing until loaded.
 */
export function CardLifecycleStrip({ cardId, compact = true }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!cardId) return undefined;
    let alive = true;
    api
      .get(`/cards/${cardId}/lifecycle`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [cardId]);

  if (!data) {
    // Reserve the space so the card row doesn't reflow when the data lands.
    return <div className="h-3 w-24 mt-1.5" aria-hidden="true" />;
  }

  const { stages, money_flow } = data;

  return (
    <div
      className="flex items-center gap-3 mt-1.5"
      data-testid={`card-lifecycle-${cardId}`}
    >
      <div className="flex items-center gap-1.5" aria-label="Card lifecycle">
        {stages.map((s, i) => (
          <StageDot key={s.key} stage={s} isLast={i === stages.length - 1} />
        ))}
      </div>
      {!compact && money_flow && (
        <span className="text-[10px] text-[#7A8B82] mono">
          ${(money_flow.spent_usd || 0).toFixed(2)} spent · ${(money_flow.balance_usd || 0).toFixed(2)} left
        </span>
      )}
    </div>
  );
}


function StageDot({ stage }) {
  const tooltip = stage.done && stage.ts
    ? `${stage.label} · ${new Date(stage.ts).toLocaleString()}`
    : `${stage.label} · pending`;
  const dotClass = stage.done
    ? "bg-[#184A30] border-[#184A30]"
    : "bg-transparent border-[#CBD0CD]";
  return (
    <span
      className={`w-2 h-2 rounded-full border ${dotClass} transition`}
      title={tooltip}
      data-testid={`lifecycle-stage-${stage.key}`}
      aria-label={tooltip}
    />
  );
}


/**
 * Issuer-wide money-flow strip — one horizontal segmented bar showing
 * how much of the user's loaded $$ has been spent, vs delivered, vs
 * still in customers' wallets. Pulls /cards/lifecycle-summary once.
 */
export function MoneyFlowStrip() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api
      .get(`/cards-summary/lifecycle`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, []);

  if (!data || !data.total_cards) return null;

  const loaded = Math.max(data.loaded_usd || 0, 0.01);
  const spent = Math.min(data.spent_usd || 0, loaded);
  const remaining = Math.max(loaded - spent, 0);
  const spentPct = (spent / loaded) * 100;

  return (
    <div
      className="rounded-2xl border border-[#E5E0D8] bg-white p-5"
      data-testid="money-flow-strip"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h3 className="serif text-xl text-[#0A1F16]">Money flow</h3>
        <p className="text-xs text-[#4A5C50] mono">
          {data.total_cards} card{data.total_cards === 1 ? "" : "s"} · ${loaded.toFixed(2)} loaded
        </p>
      </div>

      <div className="h-2.5 rounded-full overflow-hidden bg-[#F1ECE3] flex" aria-label="Spent vs remaining balance">
        <div
          className="bg-[#CBA35C] transition-[width] duration-500"
          style={{ width: `${spentPct}%` }}
          data-testid="money-flow-spent-bar"
        />
        <div
          className="bg-[#184A30] transition-[width] duration-500"
          style={{ width: `${100 - spentPct}%` }}
          data-testid="money-flow-remaining-bar"
        />
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <Stat label="Spent" value={`$${spent.toFixed(2)}`} dot="bg-[#CBA35C]" testid="money-flow-spent" />
        <Stat label="Remaining" value={`$${remaining.toFixed(2)}`} dot="bg-[#184A30]" testid="money-flow-remaining" />
        <Stat label="Cards delivered" value={`${data.delivered_count}/${data.total_cards}`} testid="money-flow-delivered" />
        <Stat label="Cards spent" value={`${data.spent_count}/${data.total_cards}`} testid="money-flow-spent-count" />
      </div>
    </div>
  );
}


function Stat({ label, value, dot, testid }) {
  return (
    <div className="flex items-start gap-2" data-testid={testid}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full mt-1.5 ${dot}`} aria-hidden="true" />}
      <div className="min-w-0">
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A8B82]">{label}</p>
        <p className="mono text-[#0A1F16] mt-0.5">{value}</p>
      </div>
    </div>
  );
}

export default CardLifecycleStrip;
